import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LANDING_CONTENT } from '../config/landing-content';
import LandingNav from '../components/LandingNav';
import LogoLoop from '../components/LogoLoop';
import Reveal from '../components/Reveal';
import { useInView } from '../hooks/useInView';
import { useCountUp } from '../hooks/useCountUp';
import '../styles/landing.css';

/* -------------------------------------------------------------------------- */
/* Step icons                                                                  */
/* -------------------------------------------------------------------------- */

const STEP_ICONS: Record<string, React.ReactNode> = {
  target: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
    </svg>
  ),
  camera: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </svg>
  ),
  rocket: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
      <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
      <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
      <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
    </svg>
  ),
};

const HeartIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M12 21s-7.5-4.6-10-9A5.3 5.3 0 0 1 12 5a5.3 5.3 0 0 1 10 7c-2.5 4.4-10 9-10 9z" />
  </svg>
);

/* -------------------------------------------------------------------------- */
/* Rotating headline word                                                      */
/* -------------------------------------------------------------------------- */

function RotatingWord({ words }: { words: string[] }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (words.length <= 1) return;
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % words.length), 2600);
    return () => clearInterval(id);
  }, [words.length]);

  // Reserve width for the longest word so the layout doesn't jump.
  const longest = words.reduce((a, b) => (b.length > a.length ? b : a), '');

  return (
    <span className="hero-rotator" aria-live="polite">
      <span className="hero-rotator-sizer" aria-hidden>
        {longest}
      </span>
      {words.map((word, i) => (
        <span
          key={word}
          className={`hero-rotator-word${i === index ? ' hero-rotator-word--active' : ''}`}
          aria-hidden={i !== index}
        >
          {word}
        </span>
      ))}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* Hero content wall                                                           */
/* -------------------------------------------------------------------------- */

function HeroWall() {
  const frames = LANDING_CONTENT.hero.frames;
  // Split frames across three drifting columns.
  const columns = [
    { items: [frames[0], frames[3]], dir: 'up' as const, dur: 26 },
    { items: [frames[1], frames[4]], dir: 'down' as const, dur: 32 },
    { items: [frames[2], frames[5]], dir: 'up' as const, dur: 22 },
  ];

  return (
    <div className="hero-wall" aria-hidden>
      {columns.map((col, ci) => (
        <div key={ci} className={`hero-wall-col hero-wall-col--${col.dir}`}>
          <div className="hero-wall-track" style={{ animationDuration: `${col.dur}s` }}>
            {[...col.items, ...col.items].map((frame, fi) => (
              <div key={fi} className="hero-frame">
                <img
                  className="hero-frame-media"
                  src={frame.image}
                  alt={frame.alt}
                  loading="lazy"
                  decoding="async"
                  draggable={false}
                />
                <div className="hero-frame-head">
                  {'logo' in frame && frame.logo ? (
                    <span
                      className={`hero-frame-avatar hero-frame-avatar--logo${
                        'logoFit' in frame && frame.logoFit === 'contain'
                          ? ' hero-frame-avatar--contain'
                          : ''
                      }`}
                    >
                      <img src={frame.logo} alt="" loading="lazy" decoding="async" draggable={false} />
                    </span>
                  ) : (
                    <span className="hero-frame-avatar" style={{ background: frame.avatarColor }}>
                      {frame.avatarLetter}
                    </span>
                  )}
                  <span className="hero-frame-handle">{frame.handle}</span>
                </div>
                <div className="hero-frame-foot">
                  {frame.caption && <span className="hero-frame-caption">{frame.caption}</span>}
                  <span className="hero-frame-likes">
                    <HeartIcon />
                    {frame.likes}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Count-up stat                                                               */
/* -------------------------------------------------------------------------- */

function parseStat(value: string) {
  const match = value.match(/^(\D*)(\d+(?:\.\d+)?)(.*)$/);
  if (!match) return { prefix: '', target: null as number | null, suffix: value, decimals: 0 };
  const [, prefix, num, suffix] = match;
  const decimals = num.includes('.') ? num.split('.')[1].length : 0;
  return { prefix, target: parseFloat(num), suffix, decimals };
}

function CountUpStat({ value, active }: { value: string; active: boolean }) {
  const { prefix, target, suffix, decimals } = parseStat(value);
  const current = useCountUp(target ?? 0, active && target !== null);

  if (target === null) return <>{value}</>;

  return (
    <>
      {prefix}
      {current.toFixed(decimals)}
      {suffix}
    </>
  );
}

function StatsGrid() {
  const { ref, inView } = useInView<HTMLDivElement>({ threshold: 0.3 });

  return (
    <div className="landing-stats-grid" ref={ref}>
      {LANDING_CONTENT.stats.metrics.map((metric, index) => (
        <div
          key={index}
          className={`landing-stat-card reveal${inView ? ' reveal--visible' : ''}`}
          style={{ transitionDelay: `${index * 120}ms` }}
        >
          <div className="landing-stat-value">
            <CountUpStat value={metric.value} active={inView} />
          </div>
          <div className="landing-stat-label">{metric.label}</div>
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* How it works (with animated connector line)                                 */
/* -------------------------------------------------------------------------- */

function HowItWorks({ onGetStarted }: { onGetStarted: () => void }) {
  const { ref, inView } = useInView<HTMLDivElement>({ threshold: 0.25 });

  return (
    <section className="landing-section landing-how-it-works">
      <div className="landing-section-content">
        <Reveal as="div">
          <h2 className="landing-section-heading">{LANDING_CONTENT.howItWorks.heading}</h2>
        </Reveal>
        <div className={`landing-steps-grid${inView ? ' is-visible' : ''}`} ref={ref}>
          <span className="landing-steps-connector" aria-hidden />
          {LANDING_CONTENT.howItWorks.steps.map((step, index) => (
            <div
              key={step.number}
              className={`landing-step-card reveal${inView ? ' reveal--visible' : ''}`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              <div className="landing-step-icon">{STEP_ICONS[step.icon]}</div>
              <div className="landing-step-number">{step.number}</div>
              <h3 className="landing-step-title">{step.title}</h3>
              <p className="landing-step-description">{step.description}</p>
            </div>
          ))}
        </div>
        <Reveal>
          <p className="landing-tagline">{LANDING_CONTENT.howItWorks.tagline}</p>
          <button onClick={onGetStarted} className="landing-cta-button landing-cta-secondary">
            {LANDING_CONTENT.howItWorks.cta}
          </button>
        </Reveal>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/* Page                                                                        */
/* -------------------------------------------------------------------------- */

export default function LandingPage() {
  const navigate = useNavigate();

  const handleGetStarted = () => navigate('/contact');
  const handleLogin = () => navigate('/login');

  return (
    <div className="landing-page">
      <LandingNav />

      {/* Hero Section */}
      <section className="landing-hero">
        <div className="landing-hero-aurora" aria-hidden />
        <div className="landing-hero-grid">
          <div className="landing-hero-copy">
            <span className="landing-hero-eyebrow">{LANDING_CONTENT.hero.eyebrow}</span>
            <h1 className="landing-hero-title">
              {LANDING_CONTENT.hero.titleStatic}{' '}
              <RotatingWord words={LANDING_CONTENT.hero.rotatingWords} />
            </h1>
            <p className="landing-hero-subtitle">{LANDING_CONTENT.hero.subheading}</p>
            <div className="landing-hero-cta-group">
              <button onClick={handleGetStarted} className="landing-cta-button landing-cta-filled">
                {LANDING_CONTENT.hero.cta}
              </button>
              <button onClick={handleLogin} className="landing-cta-button">
                Log In
              </button>
            </div>
          </div>
          <HeroWall />
        </div>
      </section>

      {/* Brand Heartbeat */}
      <section className="landing-section landing-heartbeat">
        <div className="landing-section-content">
          <Reveal>
            <h2 className="landing-section-heading">{LANDING_CONTENT.heartbeat.heading}</h2>
          </Reveal>
          {LANDING_CONTENT.heartbeat.body.map((paragraph, index) => (
            <Reveal key={index} delay={index * 100}>
              <p className="landing-body-text">{paragraph}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <HowItWorks onGetStarted={handleGetStarted} />

      {/* Stats */}
      <section className="landing-section landing-stats">
        <div className="landing-section-content">
          <Reveal>
            <h2 className="landing-section-heading">{LANDING_CONTENT.stats.heading}</h2>
          </Reveal>
          <StatsGrid />
        </div>
      </section>

      {/* Modern Social Reality */}
      <section className="landing-section landing-reality">
        <div className="landing-section-content">
          <Reveal>
            <h2 className="landing-section-heading">{LANDING_CONTENT.modernReality.heading}</h2>
          </Reveal>
          {LANDING_CONTENT.modernReality.body.map((paragraph, index) => (
            <Reveal key={index} delay={index * 100}>
              <p className="landing-body-text">{paragraph}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Brands */}
      <section className="landing-section landing-brands">
        <div className="landing-section-content">
          <Reveal>
            <h2 className="landing-section-heading">{LANDING_CONTENT.brands.heading}</h2>
          </Reveal>
          <div className="landing-brands-loop-container">
            <LogoLoop
              logos={LANDING_CONTENT.brands.companies.map((company) => ({
                src: company.logo,
                alt: company.name,
                title: company.name,
                href: company.link,
              }))}
              speed={50}
              direction="left"
              logoHeight={60}
              gap={80}
              pauseOnHover
              fadeOut
              fadeOutColor="#ffffff"
              scaleOnHover
              ariaLabel="Brands using CrewCast for employee-generated content"
            />
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="landing-section landing-final-cta">
        <div className="landing-hero-aurora landing-hero-aurora--soft" aria-hidden />
        <div className="landing-section-content">
          <Reveal>
            <h2 className="landing-section-heading">{LANDING_CONTENT.finalCta.heading}</h2>
            <p className="landing-body-text landing-final-subheading">
              {LANDING_CONTENT.finalCta.subheading}
            </p>
            <button onClick={handleGetStarted} className="landing-cta-button landing-cta-large landing-cta-filled">
              {LANDING_CONTENT.finalCta.cta}
            </button>
          </Reveal>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-footer-content">
          <div className="landing-footer-links">
            <button onClick={() => navigate('/contact')} className="landing-footer-link">
              Contact
            </button>
            <button onClick={() => navigate('/blog')} className="landing-footer-link">
              Blog
            </button>
            <button onClick={() => navigate('/privacy')} className="landing-footer-link">
              Privacy Policy
            </button>
          </div>
          <div className="landing-footer-copyright">
            © {new Date().getFullYear()} CrewCast. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
